import { redeemCoupon } from '@/api/account';
import { IPointsMallInfo } from '@/api/types/account';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { toast } from '@/components/Toast';
import { useStore } from '@/providers/app-store-provider';
import { font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import React, { useContext, useState } from 'react';

// 直接定义需要的属性，不使用extends
interface CouponDetail {
  id: number;
  point: number;
  [key: string]: any; // 允许其他属性
}

interface FixedBottomButtonProps {
  detail: CouponDetail;
  refreshFreeData?: () => void;
  pointsMallData?: IPointsMallInfo & {
    points?: number;
  };
  containerStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
}

const FixedBottomButton: React.FC<FixedBottomButtonProps> = ({
  detail,
  refreshFreeData,
  pointsMallData,
  containerStyle,
  buttonStyle,
}) => {
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const token = useStore((state) => state.token);
  const clientUserInfo = useStore((state) => state.userInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const [joinMemberLoading, setJoinMemberLoading] = useState(false);
  const t = useTranslations('account.pointsMall.fixedBottomButton');
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const { deviceType } = useContext(ResponsiveContext);
  const setJoinFreeDialogInfo = useStore((state) => state.setJoinFreeDialogInfo);

  const FixedButtonContainer = styled(Box)(() => ({
    position: deviceType === EPlatform.WEB ? 'absolute' : 'fixed',
    bottom: deviceType === EPlatform.WEB ? '-16px' : '0',
    right: deviceType === EPlatform.WEB ? 'auto' : '0',
    width: deviceType === EPlatform.WEB ? 'calc(100% - 478px)' : '100%',
    left: deviceType === EPlatform.WEB ? '432px' : 0,
    backgroundColor: '#ffffff',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    flexDirection: deviceType === EPlatform.WEB ? 'row' : 'row-reverse',
    paddingBottom: deviceType === EPlatform.WEB ? '16px' : '0',
    paddingTop: deviceType === EPlatform.WEB ? '16px' : '0',
    ...containerStyle,
  }));
  const SelectButton = styled(Button)(() => ({
    borderRadius: '4px',
    height: '48px',
    fontSize: '14px',
    textTransform: 'none',
    width: '100%',
    fontFamily: font.bb,
    display: 'block',
    ...buttonStyle,
  }));

  const signIn = () => {
    if (token) {
      handleJoinMember();
    } else {
      setShowLoginRegisterDialog(true);
    }
  };

  const handleJoinMember = async () => {
    setJoinMemberLoading(true);
    setJoinFreeDialogInfo({
      show: true,
    });
    setJoinMemberLoading(false);
    setTimeout(() => {
      refreshAccountInfo();
    }, 3000);
  };

  const handleRedeem = async () => {
    try {
      setBtnLoading(true);
      const { code, msg } = await redeemCoupon({ discount_code_id: detail.id });
      if (code === 200) {
        toast.success(msg);
      } else {
        toast.error(msg);
      }
      await refreshAccountInfo();
      if (refreshFreeData) {
        refreshFreeData();
      }
    } catch (error) {
      logger.error('Failed to redeem coupon:', error);
    } finally {
      setBtnLoading(false);
    }
  };

  const renderMemberSelectButton = () => {
    return Number(pointsMallData?.points || 0) >= Number(detail.point) ? (
      <SelectButton
        color="primary"
        onClick={handleRedeem}
        loading={btnLoading}
        variant="contained"
        disabled={btnLoading}
      >
        {t('redeem')}
      </SelectButton>
    ) : (
      <SelectButton variant="contained" disabled>
        <Box sx={{ color: '#ffffff' }}>{t('insufficientPoints')}</Box>
      </SelectButton>
    );
  };

  return (
    <>
      <FixedButtonContainer>
        {(() => {
          // 非会员或未登录
          if (clientUserInfo.is_member !== 1 || !token) {
            return (
              <SelectButton
                color="primary"
                loading={joinMemberLoading}
                onClick={signIn}
                variant="contained"
              >
                {t('joinForFree')}
              </SelectButton>
            );
          }
          // 会员可用按钮
          else if (clientUserInfo.is_member === 1) {
            return renderMemberSelectButton();
          }
          // 兜底情况
          return null;
        })()}
      </FixedButtonContainer>
    </>
  );
};

export default FixedBottomButton;
